import {
  laptopMaxXL,
  laptopMediumMax,
  laptopMediumMin,
  laptopMinXL,
} from '@propertypal/shared/src/constants/mediaQueries';
import DistrictMap from '@propertypal/shared/src/resources/images/electoral-maps/ni-map.svg';
import { ElectoralArea, ElectoralDistrict } from '@propertypal/shared/src/types/electoral';
import styled, { css } from 'styled-components';
import { MAX_WIDTH } from '../layout/PageWrapper.style';

export const Wrapper = styled.div`
  margin: 0 auto 60px;
  max-width: ${MAX_WIDTH}px;
  width: 100%;
  padding: 0 30px;

  @media (${laptopMediumMax}) {
    padding: 0 15px;
    text-align: center;
  }
`;

export const LoadingBox = styled.div<{ bg?: string; minHeight?: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  ${({ bg }) =>
    bg
      ? css`
          background: ${bg};
        `
      : ''}
  width: 100%;
  ${({ minHeight }) =>
    minHeight
      ? css`
          min-height: ${minHeight}px;
        `
      : ''}
`;

export const Content = styled.div<{ mt?: number; mobGap?: number }>`
  width: 100%;
  display: flex;
  gap: 10px;

  ${({ mt }) =>
    mt
      ? css`
          margin-top: ${mt}px;
        `
      : ''}

  @media (${laptopMediumMax}) {
    flex-direction: column;
    gap: ${({ mobGap }) => mobGap || 60}px;
  }
`;

export const MapContent = styled.div`
  flex: 1;
  position: relative;
  display: flex;

  svg {
    z-index: 1;
  }

  @media (${laptopMaxXL}) and (${laptopMediumMin}) {
    align-self: center;
  }

  @media (${laptopMediumMax}) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 500px;
    width: 100%;
    max-width: 500px;
    margin: 0 auto 60px auto;
    gap: 30px;
  }
`;

export const StatsContent = styled.div<{ spaceAround?: boolean; small?: boolean }>`
  display: flex;

  @media (${laptopMediumMax}) {
    gap: 20px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
  }

  @media (${laptopMediumMin}) {
    flex-direction: column;
    width: 350px;
    flex-basis: 350px;
    border-left: 1px solid ${({ theme }) => theme.backgroundLight};
    padding-left: 35px;

    ${({ spaceAround, small }) =>
      spaceAround
        ? css`
            gap: 20px;
            padding-top: ${small ? 0 : 10}px;
          `
        : css`
            padding-bottom: 60px;
          `}
  }
`;

export const StyleStats = styled.div<{ small?: boolean }>`
  display: flex;
  align-items: flex-start;
  flex-direction: column;

  ${({ small }) =>
    !small
      ? css`
          @media (${laptopMediumMin}) {
            margin-bottom: 30px;
          }
        `
      : ''}
`;

type MapProps = { area?: ElectoralArea['id']; $priceTracker?: boolean };

export const AreaMap = styled.div<MapProps>`
  width: 100%;

  ${({ $priceTracker }) =>
    $priceTracker
      ? css`
          max-height: 500px;
        `
      : css`
          @media (${laptopMinXL}) {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            right: 0;
            max-height: 100%;
            max-width: calc(100% - 310px);
          }

          @media (${laptopMaxXL}) and (${laptopMediumMin}) {
            max-width: 50%;
          }
        `}

  ${({ area, theme }) =>
    area
      ? css`
          [data-id='${area}'] {
            fill: ${theme.primary};
            opacity: 1;
            path {
              fill: ${theme.primary};
              opacity: 1;
            }
          }

          [data-id='${area}_text'] {
            fill: ${theme.white};
            path {
              fill: ${theme.white};
            }
          }
        `
      : ''}
`;

/**
 * If the ni-map.svg ever changes, you will need to add all district codes as data-id attributes onto each g element
 * and apply the district code appended with '_text' as a data-id atribute onto all district text g elements
 * so the styles below apply
 */
export const NiMap = styled(DistrictMap)<{ district: ElectoralDistrict['id'] }>`
  width: 100%;

  @media (${laptopMinXL}) {
    position: absolute;
    top: 0;
    left: 0;
    max-width: 310px;
  }

  @media (${laptopMaxXL}) and (${laptopMediumMin}) {
    max-width: 50%;
    height: max-content;
  }

  ${({ district, theme }) =>
    district
      ? css`
          [data-id='${district}'] {
            fill: ${theme.primary};
            opacity: 1;
            path {
              fill: ${theme.primary};
              opacity: 1;
            }
          }
        `
      : ''}

  /* Don't update colour for Belfast or ards & north down */
  ${({ district, theme }) =>
    !['N09000003', 'N09000011'].includes(district)
      ? css`
          [data-id='${district}_text'] {
            fill: ${theme.white};
            path {
              fill: ${theme.white};
            }
          }
        `
      : ''}
`;

const areaBox = css`
  position: absolute;
  padding: 5px;
  border-radius: 4px;
  max-width: 160px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.2);
  background: ${({ theme }) => theme.white};
`;

export const CouncilBox = styled.div`
  ${areaBox}
  top: -10px;
  left: 0;
`;

export interface AreaBoxProps {
  top?: number;
  topTab?: number;
  bottom?: number;
  bottomTab?: number;
  right?: number | string;
  rightTab?: number;
  maxW?: number;
  leftMob?: number;
  bottomMob?: number;
}

export const AreaBox = styled.div<AreaBoxProps>`
  ${areaBox}
  width: 100%;
  max-width: ${({ maxW }) => maxW || 160}px;

  @media (${laptopMediumMin}) {
    ${({ top, bottom }) =>
      top != null
        ? css`
            top: ${top ?? 0}px;
          `
        : css`
            bottom: ${bottom ?? 0}px;
          `}
    right: ${({ right }) => (right ? (typeof right === 'number' ? `${right}px` : right) : 0)};
  }

  @media (${laptopMaxXL}) and (${laptopMediumMin}) {
    ${({ top, topTab, bottom, bottomTab }) =>
      top != null
        ? css`
            top: ${topTab ?? top ?? 0}px;
          `
        : css`
            bottom: ${bottomTab ?? bottom ?? 0}px;
          `}
    right: ${({ right, rightTab }) => rightTab ?? right ?? 0}px;
  }

  @media (${laptopMediumMax}) {
    bottom: ${({ bottomMob }) => bottomMob ?? -40}px;
    ${({ leftMob, right }) =>
      leftMob != null
        ? css`
            left: ${leftMob ?? 0}px;
          `
        : css`
            right: ${right ?? 0}px;
          `}
  }
`;

export const MarketTrendsCheckboxLabel = styled.span<{ colour: string }>`
  text-decoration: underline;
  text-decoration-color: ${({ colour }) => colour};
  text-underline-offset: 6px;
  text-decoration-thickness: 2px;
`;
